import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, ArrowLeft, CheckCircle2 } from "lucide-react";
import api from "../lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setError("");
    setSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { email: email.toLowerCase().trim() });
      setDone(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : "Une erreur est survenue. Veuillez réessayer."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6 py-16 bg-[#001F3F]"
      data-testid="forgot-password-page"
    >
      <div className="w-full max-w-md">
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-white/60 hover:text-white mb-8"
          data-testid="forgot-back-login"
        >
          <ArrowLeft size={12} /> Retour à la connexion
        </Link>

        <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#C5CDD8]">
          Sécurité du compte
        </div>
        <h1 className="font-display mt-3 text-3xl sm:text-4xl font-bold uppercase tracking-tighter text-white">
          Mot de passe<br />oublié ?
        </h1>

        {done ? (
          <div className="mt-10 border border-emerald-500/30 bg-emerald-500/5 p-6" data-testid="forgot-success">
            <CheckCircle2 size={28} strokeWidth={1.5} className="text-emerald-400" />
            <h2 className="font-display mt-4 text-lg font-bold uppercase tracking-tight text-white">
              Vérifiez votre boîte mail
            </h2>
            <p className="mt-2 text-sm text-white/70 leading-relaxed">
              Si un compte est associé à <strong className="text-white">{email}</strong>, un lien de réinitialisation vient de vous être envoyé.
              Vérifiez aussi votre dossier <strong className="text-white">spam/courrier indésirable</strong>.
            </p>
            <p className="mt-3 text-xs text-white/45">
              Le lien expire dans 15 minutes.
            </p>
            <Link
              to="/login"
              className="mt-6 block text-center border border-white/30 hover:bg-white/10 transition uppercase tracking-[0.25em] text-xs font-bold text-white px-6 py-3"
            >
              Retour à la connexion
            </Link>
          </div>
        ) : (
          <>
            <p className="mt-5 text-sm text-white/65 leading-relaxed">
              Saisissez l'email de votre compte. Vous recevrez un lien pour choisir un nouveau mot de passe.
            </p>
            <form onSubmit={handleSubmit} className="mt-8 space-y-5" data-testid="forgot-form">
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/55 mb-2 block">
                  Email
                </label>
                <div className="relative">
                  <Mail
                    size={14}
                    strokeWidth={1.5}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40"
                  />
                  <input
                    type="email"
                    required
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-transparent border border-white/30 text-white px-10 py-3 focus:outline-none focus:border-white"
                    placeholder="vous@exemple.com"
                    data-testid="forgot-email-input"
                  />
                </div>
              </div>

              {error && (
                <div
                  className="text-xs text-[#FF6B6B] border border-[#FF6B6B]/30 bg-[#FF6B6B]/5 px-3 py-2"
                  data-testid="forgot-error"
                >
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting || !email}
                className="w-full border border-white bg-white text-[#001F3F] hover:bg-[#FFD700] hover:border-[#FFD700] transition-all uppercase tracking-[0.25em] text-xs font-bold py-4 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="forgot-submit-btn"
              >
                {submitting ? "Envoi en cours…" : "Recevoir le lien"}
              </button>
            </form>

            <p className="mt-8 text-xs text-white/40 leading-relaxed">
              Pas de compte ?{" "}
              <Link
                to="/register"
                className="text-white underline underline-offset-4 hover:text-[#FFD700]"
              >
                Créer un compte
              </Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
